/**
 * Pure view model for the `/pools` list pane.
 *
 * Input is `/pool/list-mine`'s response items plus the current search query.
 * `statusLabel` is the server's one written summary string and is rendered
 * verbatim per decision C18 — this module never parses, formats or branches
 * on a contest rule.
 */
import {
  CUSTOM_LEAGUE_LABEL,
  isCustomLeagueKey,
} from "@sportsmet/contest-engine";
import {
  poolEligibleLeagueOptions,
  type typeCatalogSport,
} from "../createPool/createPoolDraft";

export type typePoolListRow = {
  bannerImageUrl?: string | null | undefined;
  creatorHandle?: string | null | undefined;
  poolId: string;
  name: string;
  sportKey: string;
  leagueKey: string;
  seasonYear: number;
  contestType: string;
  poolRole: string;
  membershipStatus: string;
  poolStatus: string;
  statusLabel: string;
  activeMemberCount: number;
  /** ISSUE-1343. The fan archived this pool in their own list. */
  isArchivedByMe?: boolean | undefined;
};

export type typePoolMarkTone =
  "primary" | "accent" | "success" | "warning" | "info" | "neutral";

export type typePoolStatusTone = "ok" | "warning" | "neutral";

export type typePoolCardModel = {
  adminRole?: "owner" | "admin";
  sportKey?: string;
  bannerImageUrl?: string | null | undefined;
  creatorHandle?: string | null | undefined;
  contestType?: string;
  poolId: string;
  name: string;
  /** Two uppercase letters for the mark plate, or "?" for an unnamed pool. */
  initials: string;
  markTone: typePoolMarkTone;
  /** "NFL", the catalog's own abbreviation for the pool's leagueKey. "?"
   *  when the league carries no catalog entry. */
  leagueLabel: string;
  /** "Survivor". */
  contestLabel: string;
  /** "14 members" / "1 member". */
  membersLabel: string;
  /** "2025 season". */
  seasonLabel: string;
  /** The server's string, verbatim. Never parsed. */
  statusLabel: string;
  statusTone: typePoolStatusTone;
  /** False for an archived pool or a pending / suspended membership. */
  isSelectable: boolean;
  /**
   * True while the fan's request to join is waiting. The card then opens the
   * pool's preview with Cancel request instead of the pool (ISSUE-1341).
   */
  isPendingRequest: boolean;
  /**
   * ISSUE-1343. The fan archived this pool in their own list, so its menu
   * offers Unarchive rather than Archive. The server's flag, verbatim.
   * Absent reads as not archived.
   */
  isArchivedByMe?: boolean | undefined;
};

export type typePoolsListState =
  | { phase: "loading" }
  | { phase: "failed" }
  | { phase: "empty" }
  | { phase: "noResults"; query: string }
  | { phase: "ready"; cards: typePoolCardModel[] };

const MARK_TONES: readonly typePoolMarkTone[] = [
  "primary",
  "accent",
  "success",
  "warning",
  "info",
  "neutral",
];

const CONTEST_TYPE_LABELS: Readonly<Record<string, string>> = {
  survivor: "Survivor",
  squares: "Squares",
  stripCard: "Strip Card",
  pickem: "Pick'em",
};

export function poolMarkInitials(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter((part) => part.length > 0);
  if (parts.length === 0) {
    return "?";
  }
  const first = parts[0] ?? "";
  if (parts.length === 1) {
    return first.slice(0, 2).toUpperCase();
  }
  const second = parts[1] ?? "";
  return `${first.charAt(0)}${second.charAt(0)}`.toUpperCase();
}

export function poolMarkTone(poolId: string): typePoolMarkTone {
  let sum = 0;
  for (let i = 0; i < poolId.length; i += 1) {
    sum += poolId.charCodeAt(i);
  }
  return MARK_TONES[sum % MARK_TONES.length] ?? "primary";
}

/** leagueKey -> the catalog's `abbreviation` field for that league. Built by
 *  `leagueAbbreviationsByKey` from `POST /league-catalog/get`'s response. */
export type typeLeagueAbbreviations = Readonly<Record<string, string>>;

/** The catalog shape this module reads to build a leagueKey -> abbreviation
 *  lookup. A structural subset of POST /league-catalog/get's response,
 *  naming only the fields used. */
export type typeLeagueCatalogSports = readonly {
  leagues: readonly { leagueKey: string; abbreviation: string }[];
}[];

/** Flattens the catalog's sports/leagues tree into one leagueKey ->
 *  abbreviation lookup, the source ISSUE-770 moved `leagueShortLabel` to
 *  read instead of slicing the key. */
export function leagueAbbreviationsByKey(
  sports: typeLeagueCatalogSports,
): typeLeagueAbbreviations {
  const map: Record<string, string> = {};
  for (const sport of sports) {
    for (const league of sport.leagues) {
      map[league.leagueKey] = league.abbreviation;
    }
  }
  return map;
}

/**
 * The league badge text: the catalog's `abbreviation` for `leagueKey`, e.g.
 * "NFL". Returns "?" when `leagueKey` has no catalog entry (ISSUE-770 R4),
 * rather than a partial key with a trailing hyphen.
 *
 * `abbreviations` is optional only so a caller not yet wired to
 * `POST /league-catalog/get` keeps its pre-ISSUE-770 behavior (deriving a
 * label by slicing `leagueKey`) instead of silently downgrading to "?"
 * everywhere. `poolHomeState.ts`'s pool header model is the one remaining
 * caller in that state; see ISSUE-770's report for why it was left out of
 * this fix's scope.
 */
export function leagueShortLabel(
  leagueKey: string,
  abbreviations?: typeLeagueAbbreviations,
): string {
  // ISSUE-1299. A pool with no league is not in the catalog; it reads Custom.
  if (isCustomLeagueKey(leagueKey)) return CUSTOM_LEAGUE_LABEL;
  if (abbreviations !== undefined) {
    return abbreviations[leagueKey] ?? "?";
  }
  if (leagueKey === "") {
    return "?";
  }
  const dashIndex = leagueKey.indexOf("-");
  const afterDash =
    dashIndex === -1 ? leagueKey : leagueKey.slice(dashIndex + 1);
  return afterDash.toUpperCase().slice(0, 4);
}

export function contestTypeLabel(contestType: string): string {
  const known = CONTEST_TYPE_LABELS[contestType];
  if (known !== undefined) {
    return known;
  }
  if (contestType === "") {
    return contestType;
  }
  return `${contestType.charAt(0).toUpperCase()}${contestType.slice(1)}`;
}

export function memberCountLabel(count: number): string {
  return count === 1 ? "1 member" : `${count} members`;
}

export function toPoolCardModel(
  row: typePoolListRow,
  leagueAbbreviations?: typeLeagueAbbreviations,
): typePoolCardModel {
  let statusTone: typePoolStatusTone;
  let isSelectable: boolean;
  if (row.poolStatus === "archived") {
    statusTone = "neutral";
    isSelectable = false;
  } else if (row.membershipStatus === "pending") {
    statusTone = "warning";
    isSelectable = false;
  } else if (row.membershipStatus === "suspended") {
    statusTone = "warning";
    isSelectable = false;
  } else {
    statusTone = "ok";
    isSelectable = true;
  }

  return {
    poolId: row.poolId,
    bannerImageUrl: row.bannerImageUrl,
    creatorHandle: row.creatorHandle,
    name: row.name,
    ...(row.poolRole === "owner" || row.poolRole === "admin"
      ? { adminRole: row.poolRole }
      : {}),
    sportKey: row.sportKey,
    contestType: row.contestType,
    initials: poolMarkInitials(row.name),
    markTone: poolMarkTone(row.poolId),
    leagueLabel: leagueShortLabel(row.leagueKey, leagueAbbreviations),
    contestLabel: contestTypeLabel(row.contestType),
    membersLabel: memberCountLabel(row.activeMemberCount),
    seasonLabel: `${row.seasonYear} season`,
    statusLabel: row.statusLabel,
    statusTone,
    isSelectable,
    isPendingRequest:
      row.poolStatus !== "archived" && row.membershipStatus === "pending",
    isArchivedByMe: row.isArchivedByMe === true,
  };
}

export function resolvePoolsListState(input: {
  isLoading: boolean;
  hasFailed: boolean;
  items: readonly typePoolListRow[] | null;
  query: string;
  /** leagueKey -> abbreviation, from POST /league-catalog/get. */
  leagueAbbreviations?: typeLeagueAbbreviations | undefined;
}): typePoolsListState {
  if (input.hasFailed) {
    return { phase: "failed" };
  }
  if (input.isLoading) {
    return { phase: "loading" };
  }
  if (input.items === null || input.items.length === 0) {
    return { phase: "empty" };
  }

  const trimmedQuery = input.query.trim();
  const cards = input.items.map((row) =>
    toPoolCardModel(row, input.leagueAbbreviations),
  );
  const filtered =
    trimmedQuery === ""
      ? cards
      : cards.filter((card) =>
          card.name.toLowerCase().includes(trimmedQuery.toLowerCase()),
        );

  if (filtered.length === 0 && trimmedQuery !== "") {
    return { phase: "noResults", query: trimmedQuery };
  }

  const sorted = [...filtered].sort((a, b) => {
    if (a.isSelectable !== b.isSelectable) {
      return a.isSelectable ? -1 : 1;
    }
    return a.name.localeCompare(b.name);
  });

  return { phase: "ready", cards: sorted };
}

/**
 * Whether the pools screen can offer Create pool right now.
 *
 * ISSUE-782, Q-782-A answer B: when no league is open for new pools the
 * control is offered disabled with the reason beside it, rather than opening
 * a wizard whose first step can never be completed.
 *
 * The eligibility test is `poolEligibleLeagueOptions`, the same derivation the
 * wizard's own first step uses, so the control and the catalog cannot drift
 * apart.
 *
 * Only a catalog that actually answered can close the control. Loading,
 * failed, and offline all leave `sports` null, and none of them is a reason to
 * tell a fan no league is open when that may not be true. A failed catalog is
 * the wizard's own retry banner to handle.
 *
 * The null check is load-bearing rather than redundant with `isLoading`. With
 * TanStack's default `networkMode: "online"` an offline query with a cold
 * cache is *paused*, not fetching, so `isLoading` and `isError` are both false
 * while `sports` is still null.
 */
export function isCreatePoolAvailable(input: {
  isLoading: boolean;
  hasFailed: boolean;
  sports: readonly typeCatalogSport[] | null;
}): boolean {
  if (input.isLoading || input.hasFailed) return true;
  if (input.sports === null) return true;
  return poolEligibleLeagueOptions(input.sports).length > 0;
}
